import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { request } from "@/lib/request";

type SignedFileUrlResponse = {
  data: {
    fileId: string;
    url: string;
    expiresAt: string;
  };
};

type SignedUrlCacheEntry = {
  url: string;
  expiresAt: string;
};

const CACHE_BUFFER_MS = 30 * 1000;
const PREFETCH_CONCURRENCY = 4;

export const useFileSignedUrlMap = ({
  fileIds,
  enabled,
}: {
  fileIds: string[];
  enabled: boolean;
}) => {
  const [signedUrlByFileId, setSignedUrlByFileId] = useState<
    Record<string, string>
  >({});
  const [errorByFileId, setErrorByFileId] = useState<Record<string, boolean>>(
    {},
  );

  const cacheByFileIdRef = useRef<Record<string, SignedUrlCacheEntry>>({});
  const inFlightByFileIdRef = useRef<Record<string, Promise<string | null>>>({});

  const normalizedFileIds = useMemo(
    () => [...new Set(fileIds.filter(Boolean))],
    [fileIds],
  );

  const hasValidCache = useCallback((fileId: string): boolean => {
    const cached = cacheByFileIdRef.current[fileId];

    if (!cached) {
      return false;
    }

    const expiresAt = new Date(cached.expiresAt).getTime();

    if (Number.isNaN(expiresAt)) {
      return false;
    }

    return expiresAt - Date.now() > CACHE_BUFFER_MS;
  }, []);

  const ensureSignedUrl = useCallback(
    async (fileId: string): Promise<string | null> => {
      if (!fileId) {
        return null;
      }

      if (hasValidCache(fileId)) {
        return cacheByFileIdRef.current[fileId]?.url || null;
      }

      const inFlight = inFlightByFileIdRef.current[fileId];
      if (inFlight) {
        return inFlight;
      }

      const requestPromise = (async () => {
        try {
          const response = await request.get<SignedFileUrlResponse>(
            `/files/${fileId}`,
          );

          const next = {
            url: response.data.data.url,
            expiresAt: response.data.data.expiresAt,
          };

          cacheByFileIdRef.current[fileId] = next;
          setSignedUrlByFileId(prev => ({
            ...prev,
            [fileId]: next.url,
          }));
          setErrorByFileId(prev => {
            if (!prev[fileId]) {
              return prev;
            }

            const nextErrors = { ...prev };
            delete nextErrors[fileId];
            return nextErrors;
          });

          return next.url;
        } catch {
          setErrorByFileId(prev => ({
            ...prev,
            [fileId]: true,
          }));
          return null;
        } finally {
          delete inFlightByFileIdRef.current[fileId];
        }
      })();

      inFlightByFileIdRef.current[fileId] = requestPromise;
      return requestPromise;
    },
    [hasValidCache],
  );

  useEffect(() => {
    if (!enabled || !normalizedFileIds.length) {
      return;
    }

    let isCancelled = false;

    const prefetch = async () => {
      let cursor = 0;
      const worker = async () => {
        while (cursor < normalizedFileIds.length && !isCancelled) {
          const nextFileId = normalizedFileIds[cursor];
          cursor += 1;

          if (!nextFileId) {
            return;
          }

          await ensureSignedUrl(nextFileId);
        }
      };

      const concurrency = Math.min(
        PREFETCH_CONCURRENCY,
        normalizedFileIds.length,
      );

      await Promise.all(Array.from({ length: concurrency }, () => worker()));
    };

    void prefetch();

    return () => {
      isCancelled = true;
    };
  }, [enabled, ensureSignedUrl, normalizedFileIds]);

  return {
    signedUrlByFileId,
    errorByFileId,
    ensureSignedUrl,
  };
};
